import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { formFieldStyles } from "./styles";
import { LabelWithTooltip } from "./LabelWithTooltip";

interface FormSelectFieldProps extends WithStyles<typeof formFieldStyles> {
	label: string;
	value: any;
	onChange: (value: any) => void;
	options: any[];
	valueExtractor: (option: any) => any;
	labelExtractor: (option: any) => string;
	required?: boolean;
	error?: boolean;
	helperText?: string;
	tooltip?: string;
	disabled?: boolean;
	multiple?: boolean;
}

const FormSelectFieldComponent: React.FC<FormSelectFieldProps> = ({
	label,
	value,
	onChange,
	options,
	valueExtractor,
	labelExtractor,
	required = false,
	error = false,
	helperText,
	tooltip,
	disabled = false,
	multiple = false,
	classes = {},
}) => {
	const labelFor = (selected: any) => {
		const option = options.find((o) => valueExtractor(o) === selected);
		return option ? labelExtractor(option) : selected;
	};
	const selectValue = multiple
		? Array.isArray(value)
			? value
			: []
		: value !== undefined && value !== null
		? value
		: "";
	return (
		<Mui.FormControl fullWidth variant="outlined" error={error} required={required} disabled={disabled}>
			<Mui.Typography component="label" className={classes.fieldLabel}>
				{tooltip ? <LabelWithTooltip label={label} tooltip={tooltip} /> : label}
				{required && <span className={classes.fieldLabelAsterisk}> *</span>}
			</Mui.Typography>
			<Mui.Select
				disabled={disabled}
				multiple={multiple}
				value={selectValue}
				onChange={(e) => {
					const selected = e.target.value;
					// MUI hands back a comma-joined string when the browser autofills a multiple select
					onChange(multiple && typeof selected === "string" ? selected.split(",") : selected);
				}}
				renderValue={multiple ? (selected: any[]) => selected.map(labelFor).join(", ") : undefined}
				classes={{ icon: classes.selectIcon }}
				IconComponent={Icons.ChevronDown}
				input={
					<Mui.OutlinedInput
						classes={{
							root: classes.selectInputRoot,
							focused: classes.selectFocused,
							notchedOutline: classes.selectNotchedOutline,
							input: classes.selectInput,
						}}
					/>
				}
			>
				{options.map((option) => (
					<Mui.MenuItem key={valueExtractor(option)} value={valueExtractor(option)}>
						{multiple ? (
							<>
								<Mui.Checkbox
									size="small"
									checked={selectValue.includes(valueExtractor(option))}
								/>
								<Mui.ListItemText primary={labelExtractor(option)} />
							</>
						) : (
							labelExtractor(option)
						)}
					</Mui.MenuItem>
				))}
			</Mui.Select>
			{helperText && (
				<Mui.FormHelperText
					classes={{
						root: classes.helperText,
					}}
				>
					{helperText}
				</Mui.FormHelperText>
			)}
		</Mui.FormControl>
	);
};

export const FormSelectField = withStyles(FormSelectFieldComponent, formFieldStyles);
